import React, { useState } from 'react';
import { Socket } from 'socket.io-client';

interface FeedbackModalProps {
  socket: Socket | null;
  isOpen: boolean;
  onClose: () => void;
  playerName: string;
  playerLevel: number;
}

export const FeedbackModal: React.FC<FeedbackModalProps> = ({
  socket,
  isOpen,
  onClose,
  playerName,
  playerLevel,
}) => {
  const [message, setMessage] = useState<string>('');
  const [feedbackType, setFeedbackType] = useState<'bug' | 'feedback' | 'suggestion'>('feedback');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitMessage, setSubmitMessage] = useState<string>('');
  const [submitSuccess, setSubmitSuccess] = useState<boolean | null>(null);

  const handleSubmit = (): void => {
    if (!socket || !message.trim()) {
      setSubmitMessage('Please enter a message');
      setSubmitSuccess(false);
      return;
    }

    setIsSubmitting(true);
    socket.emit(
      'feedback:submit',
      {
        message: message.trim(),
        feedbackType,
        playerName,
        playerLevel,
      },
      (response: { success: boolean; message: string }) => {
      setIsSubmitting(false);
      if (response.success) {
        setSubmitMessage('Feedback submitted. Thank you.');
        setSubmitSuccess(true);
        setTimeout(() => {
          setMessage('');
          setFeedbackType('feedback');
          setSubmitMessage('');
          setSubmitSuccess(null);
          onClose();
        }, 2000);
      } else {
        setSubmitMessage(response.message);
        setSubmitSuccess(false);
      }
      }
    );
  };

  const handleCancel = (): void => {
    setMessage('');
    setFeedbackType('feedback');
    setSubmitMessage('');
    setSubmitSuccess(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay} onClick={handleCancel}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>Report Bug / Feedback</h2>
          <button style={styles.closeBtn} onClick={handleCancel}>×</button>
        </div>

        <div style={styles.content}>
          <div style={styles.formSection}>
            <label style={styles.label}>Type:</label>
            <div style={styles.typeButtons}>
              {(['bug', 'feedback', 'suggestion'] as const).map((type) => (
                <button
                  key={type}
                  style={{
                    ...styles.typeBtn,
                    ...(feedbackType === type ? styles.typeBtnActive : {}),
                  }}
                  onClick={() => setFeedbackType(type)}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>

            <label style={styles.label}>Message:</label>
            <textarea
              style={styles.textarea}
              placeholder="Describe your bug report or feedback (max 5000 characters)..."
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, 5000))}
              onKeyDown={(e) => e.stopPropagation()}
              disabled={isSubmitting}
            />
            <div style={styles.charCount}>
              {message.length} / 5000
            </div>
          </div>

          {submitMessage && (
            <div style={{
              ...styles.message,
              color: submitSuccess === true ? '#238636' : '#f85149',
            }}>
              {submitMessage}
            </div>
          )}

          <div style={styles.buttonGroup}>
            <button
              style={{
                ...styles.btn,
                ...styles.submitBtn,
                opacity: isSubmitting ? 0.6 : 1,
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
              }}
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
            <button
              style={{
                ...styles.btn,
                ...styles.cancelBtn,
              }}
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2000,
  },
  modal: {
    backgroundColor: '#0d1117',
    border: '2px solid #30363d',
    borderRadius: 0,
    width: '500px',
    maxWidth: '90vw',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    color: '#e6edf3',
    fontFamily: 'var(--font-mono), monospace',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    borderBottom: '1px solid #30363d',
    backgroundColor: '#161b22',
  },
  title: {
    margin: 0,
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#58a6ff',
  },
  closeBtn: {
    background: 'none',
    border: '1px solid #f85149',
    color: '#f85149',
    fontSize: '20px',
    cursor: 'pointer',
    padding: '4px 8px',
    fontWeight: 'bold',
  },
  content: {
    flex: 1,
    padding: '16px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  infoSection: {
    backgroundColor: '#161b22',
    border: '1px solid #30363d',
    padding: '12px',
    borderRadius: 0,
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '6px 0',
    fontSize: '12px',
  },
  label: {
    color: '#8b949e',
    fontSize: '12px',
    fontWeight: 'bold',
    marginBottom: '6px',
    display: 'block',
  },
  value: {
    color: '#e6edf3',
  },
  formSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  typeButtons: {
    display: 'flex',
    gap: '8px',
    marginBottom: '12px',
  },
  typeBtn: {
    flex: 1,
    padding: '8px 12px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#30363d',
    backgroundColor: '#0d1117',
    color: '#8b949e',
    fontSize: '12px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.2s',
  },
  typeBtnActive: {
    borderColor: '#58a6ff',
    backgroundColor: 'rgba(88, 166, 255, 0.1)',
    color: '#58a6ff',
  },
  textarea: {
    width: '100%',
    minHeight: '120px',
    padding: '12px',
    backgroundColor: '#0d1117',
    border: '1px solid #30363d',
    color: '#e6edf3',
    fontFamily: 'inherit',
    fontSize: '12px',
    resize: 'vertical',
    boxSizing: 'border-box' as const,
  },
  charCount: {
    fontSize: '11px',
    color: '#8b949e',
    textAlign: 'right' as const,
  },
  message: {
    padding: '12px',
    backgroundColor: '#161b22',
    border: '1px solid #30363d',
    borderRadius: 0,
    fontSize: '12px',
  },
  buttonGroup: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end',
  },
  btn: {
    padding: '10px 16px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#30363d',
    fontSize: '12px',
    fontWeight: 'bold',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.2s',
  },
  submitBtn: {
    backgroundColor: '#238636',
    color: '#fff',
    borderColor: '#238636',
  },
  cancelBtn: {
    backgroundColor: '#0d1117',
    color: '#8b949e',
    borderColor: '#30363d',
  },
};
